import { createStyles, makeStyles } from '@material-ui/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import TextBlockEditModal from 'src/components/account/translations/TextBlockEditModal';
import { useAccount } from 'src/core/hooks/account';
import { setTranslatorMode, textBlockKeyForRenderedText, useTranslatorMode } from 'src/core/translatorMode';
import { isAdminContext } from 'src/core/utils';

// icons only next to visible text content — never inside form fields, hidden subtrees or our own UI
const SKIPPED_CONTAINERS = [
    '[data-translator-mode-ignore]',
    '[aria-hidden="true"]',
    '[contenteditable="true"]',
    'script',
    'style',
    'noscript',
    'textarea',
    'select',
    'option',
    'title',
    'svg',
].join(', ');
// 12.9: form fields whose placeholder or option texts came out of i18next get one icon of their own
const FIELDS = 'input[placeholder], textarea[placeholder], select';
const SKIPPED_FIELD_CONTAINERS = [
    '[data-translator-mode-ignore]',
    '[aria-hidden="true"]',
    '[contenteditable="true"]',
].join(', ');
const ICON_SIZE = 18;
const RESCAN_DELAY_MS = 150;

/** One text of a text block as the page shows it. */
export interface TranslatedEntry {
    textBlockKey: string;
    text: string;
}

interface TranslatedText {
    /** A text node (icon right of its last line) or a form field (icon on its top right corner). */
    node: Text | HTMLElement;
    entries: TranslatedEntry[];
}

export interface TranslatorModeMark {
    id: number;
    /** The text block(s) behind the icon — a select offers every option text that has one. */
    entries: TranslatedEntry[];
    left: number;
    top: number;
}

const useStyles = makeStyles(() =>
    createStyles({
        layer: { position: 'fixed', top: 0, left: 0, width: 0, height: 0, zIndex: 1450, pointerEvents: 'none' },
        mark: {
            position: 'fixed',
            width: ICON_SIZE,
            height: ICON_SIZE,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            border: '1px solid #93000b',
            background: '#fff',
            color: '#93000b',
            boxShadow: '0 1px 3px rgba(28,28,25,0.25)',
            cursor: 'pointer',
            pointerEvents: 'auto',
            '& .material-symbols-outlined': { fontSize: 12, lineHeight: 1 },
            '&:hover, &:focus-visible': { background: '#93000b', color: '#fff' },
        },
        chooser: {
            position: 'fixed',
            minWidth: 220,
            maxWidth: 360,
            maxHeight: 280,
            overflowY: 'auto',
            padding: 4,
            borderRadius: 8,
            border: '1px solid #e5e2dd',
            background: '#fff',
            boxShadow: '0 8px 24px rgba(28,28,25,0.18)',
            pointerEvents: 'auto',
            fontFamily: "'Inter', sans-serif",
        },
        chooserTitle: { padding: '6px 8px 4px', fontSize: 11, fontWeight: 700, color: '#5b403d' },
        chooserEntry: {
            display: 'block',
            width: '100%',
            padding: '6px 8px',
            border: 'none',
            borderRadius: 6,
            background: 'none',
            textAlign: 'left',
            cursor: 'pointer',
            '&:hover, &:focus-visible': { background: '#fef7f7' },
        },
        chooserText: { display: 'block', fontSize: 13, color: '#1c1c19' },
        chooserKey: { display: 'block', fontSize: 10, color: '#93000b', fontFamily: 'monospace' },
    })
);

function entryOf(text: string): TranslatedEntry | null {
    if (!text.trim()) return null;
    const textBlockKey = textBlockKeyForRenderedText(text);
    return textBlockKey ? { textBlockKey, text: text.trim() } : null;
}

/** The placeholder of an input/textarea, or every option text of a select, that came out of a text block. */
function fieldEntries(field: Element): TranslatedEntry[] {
    const texts =
        field instanceof HTMLSelectElement
            ? Array.from(field.options).map(option => option.text)
            : [field.getAttribute('placeholder') ?? ''];
    const entries: TranslatedEntry[] = [];
    texts.forEach(text => {
        const entry = entryOf(text);
        if (entry && !entries.some(known => known.textBlockKey === entry.textBlockKey)) entries.push(entry);
    });
    return entries;
}

/**
 * The rendered text nodes that came out of i18next for a text block (recorded while the mode is on),
 * plus the form fields whose placeholder or option texts did (12.9: search fields and dropdowns).
 */
export function findTranslatedTexts(root: Node): TranslatedText[] {
    const found: TranslatedText[] = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const text = node as Text;
        const entry = entryOf(text.data);
        if (!entry || !text.parentElement || text.parentElement.closest(SKIPPED_CONTAINERS)) continue;
        found.push({ node: text, entries: [entry] });
    }
    if (root instanceof Element || root instanceof Document) {
        root.querySelectorAll(FIELDS).forEach(field => {
            if (field.closest(SKIPPED_FIELD_CONTAINERS)) return;
            const entries = fieldEntries(field);
            if (entries.length > 0) found.push({ node: field as HTMLElement, entries });
        });
    }
    return found;
}

function isVisibleAt(element: Element, x: number, y: number, layer: Element | null): boolean {
    if (typeof document.elementsFromPoint !== 'function') return true;
    const top = document.elementsFromPoint(x, y).find(hit => !layer?.contains(hit));
    return !!top && (top === element || element.contains(top) || top.contains(element));
}

function isOffScreen(first: DOMRect | ClientRect, last: DOMRect | ClientRect): boolean {
    return last.bottom < 0 || first.top > window.innerHeight || last.right < 0 || first.left > window.innerWidth;
}

/**
 * One icon right of the last line of each visible text and on the top right corner of each visible
 * form field; texts under a dialog or scrolled away get none.
 */
export function placeMarks(texts: TranslatedText[], layer: Element | null): TranslatorModeMark[] {
    const range = document.createRange();
    const marks: TranslatorModeMark[] = [];
    texts.forEach(({ node, entries }) => {
        if (!node.isConnected) return;
        if (node instanceof HTMLElement) {
            const box = node.getBoundingClientRect();
            if (box.width === 0 || box.height === 0 || isOffScreen(box, box)) return;
            if (!isVisibleAt(node, box.left + box.width / 2, box.top + box.height / 2, layer)) return;
            marks.push({
                id: marks.length,
                entries,
                left: Math.min(Math.max(box.right - ICON_SIZE * 0.6, 0), window.innerWidth - ICON_SIZE),
                top: Math.max(box.top - ICON_SIZE * 0.4, 0),
            });
            return;
        }
        if (!node.parentElement) return;
        range.selectNodeContents(node);
        const rects = range.getClientRects();
        if (rects.length === 0) return;
        const first = rects[0];
        const last = rects[rects.length - 1];
        if (last.width === 0 || last.height === 0) return;
        if (isOffScreen(first, last)) return;
        if (!isVisibleAt(node.parentElement, first.left + first.width / 2, first.top + first.height / 2, layer)) return;
        marks.push({
            id: marks.length,
            entries,
            left: Math.min(last.right + 2, window.innerWidth - ICON_SIZE),
            top: last.top + (last.height - ICON_SIZE) / 2,
        });
    });
    return marks;
}

const TranslatorModeLayer: React.FC<{ onEdit: (textBlockKey: string) => void }> = ({ onEdit }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const layerRef = useRef<HTMLDivElement>(null);
    const textsRef = useRef<TranslatedText[]>([]);
    const [marks, setMarks] = useState<TranslatorModeMark[]>([]);
    // a field with several text blocks (the options of a select) first asks which one
    const [chooser, setChooser] = useState<TranslatorModeMark | null>(null);

    useEffect(() => {
        let frame = 0;
        let rescanTimer: number | undefined;
        const reposition = () => {
            if (frame) return;
            frame = window.requestAnimationFrame(() => {
                frame = 0;
                setMarks(placeMarks(textsRef.current, layerRef.current));
            });
        };
        const rescan = () => {
            if (rescanTimer !== undefined) return;
            rescanTimer = window.setTimeout(() => {
                rescanTimer = undefined;
                textsRef.current = findTranslatedTexts(document.body);
                reposition();
            }, RESCAN_DELAY_MS);
        };
        const observer = new MutationObserver(records => {
            if (records.some(record => !layerRef.current?.contains(record.target))) rescan();
        });
        observer.observe(document.body, {
            subtree: true,
            childList: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['placeholder'],
        });
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        document.addEventListener('transitionend', reposition, true);
        document.addEventListener('animationend', reposition, true);
        rescan();
        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
            document.removeEventListener('transitionend', reposition, true);
            document.removeEventListener('animationend', reposition, true);
            if (frame) window.cancelAnimationFrame(frame);
            if (rescanTimer !== undefined) window.clearTimeout(rescanTimer);
        };
    }, []);

    useEffect(() => {
        if (!chooser) return;
        const close = (event: Event) => {
            if (event instanceof KeyboardEvent && event.key !== 'Escape') return;
            if (event instanceof MouseEvent && layerRef.current?.contains(event.target as Node)) return;
            setChooser(null);
        };
        document.addEventListener('mousedown', close, true);
        document.addEventListener('keydown', close, true);
        return () => {
            document.removeEventListener('mousedown', close, true);
            document.removeEventListener('keydown', close, true);
        };
    }, [chooser]);

    const open = (mark: TranslatorModeMark) => {
        if (mark.entries.length === 1) onEdit(mark.entries[0].textBlockKey);
        else setChooser(current => (current?.id === mark.id ? null : mark));
    };

    return ReactDOM.createPortal(
        <div
            ref={layerRef}
            className={classes.layer}
            data-translator-mode-ignore=""
            data-testid="translator-mode-layer">
            {marks.map(mark => {
                const keys = mark.entries.map(entry => entry.textBlockKey).join(', ');
                return (
                    <button
                        key={mark.id}
                        type="button"
                        className={classes.mark}
                        style={{ left: mark.left, top: mark.top }}
                        aria-label={t('account.translations.translator_mode_edit_aria', { key: keys })}
                        title={keys}
                        onClick={() => open(mark)}
                        data-testid="translator-mode-edit"
                        data-text-block-key={mark.entries[0].textBlockKey}
                        data-text-block-keys={keys}>
                        <span className="material-symbols-outlined" aria-hidden="true">
                            edit
                        </span>
                    </button>
                );
            })}
            {chooser && (
                <div
                    className={classes.chooser}
                    role="menu"
                    style={{
                        left: Math.max(Math.min(chooser.left, window.innerWidth - 240), 0),
                        top: chooser.top + ICON_SIZE + 4,
                    }}
                    data-testid="translator-mode-chooser">
                    <div className={classes.chooserTitle}>{t('account.translations.translator_mode_choose')}</div>
                    {chooser.entries.map(entry => (
                        <button
                            key={entry.textBlockKey}
                            type="button"
                            role="menuitem"
                            className={classes.chooserEntry}
                            onClick={() => {
                                setChooser(null);
                                onEdit(entry.textBlockKey);
                            }}
                            data-testid="translator-mode-choice"
                            data-text-block-key={entry.textBlockKey}>
                            <span className={classes.chooserText}>{entry.text}</span>
                            <span className={classes.chooserKey}>{entry.textBlockKey}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>,
        document.body
    );
};

/**
 * R49-5 Übersetzermodus host (mounted once in the App): while the mode is on and the account is a
 * translator outside the AdminPanel it draws the edit icons in a layer of its own above the page —
 * the page DOM and its layout stay untouched — and opens "Textblock bearbeiten" for the clicked key.
 * A logout, a lost translator role or a switch to the AdminPanel turns the mode off.
 */
const TranslatorModeHost: React.FC = () => {
    const on = useTranslatorMode();
    const { isLoggedIn, hasPrivilege } = useAccount();
    const location = useLocation();
    const allowed = isLoggedIn && hasPrivilege('translator') && !isAdminContext(location.pathname);
    const [editKey, setEditKey] = useState<string | null>(null);
    const closeEditor = useCallback(() => setEditKey(null), []);

    useEffect(() => {
        if (on && !allowed) void setTranslatorMode(false);
    }, [on, allowed]);

    if (!on || !allowed) return null;
    return editKey ? (
        <TextBlockEditModal blockKey={editKey} onClose={closeEditor} />
    ) : (
        <TranslatorModeLayer onEdit={setEditKey} />
    );
};

export default TranslatorModeHost;
